import React, {useState} from "react";
import {FileUploader} from "react-drag-drop-files";
import UserService from "../services/UserService";
import {useAuth0} from "@auth0/auth0-react";
import {PageLoader} from "./PageLoader";
import '../styles/components/drag-drop.css';

const fileTypes = ["XLS", "XLSX"];

function DragDrop() {

    const {
        getAccessTokenSilently,
        user
    } = useAuth0();

    const [file, setFile] = useState(null);
    const [apiFinished, setApiFinished] = useState(true);
    const [errorMessage, setErrorMessage] = useState("");

    const handleChange = async (file) => {
        const token = await getAccessTokenSilently();
        setApiFinished(false);
        setFile(file);
        setErrorMessage("");
        UserService.upload(file, token).then(
            response => {
                console.log(response);
                const byteCharacters = atob(response.data);
                const byteNumbers = new Array(byteCharacters.length);
                for (let i = 0; i < byteCharacters.length; i++) {
                    byteNumbers[i] = byteCharacters.charCodeAt(i);
                }

                const byteArray = new Uint8Array(byteNumbers);
                setApiFinished(true);
                const link = document.createElement("a");
                let fileName = "farelanes-rfq-" + new Date().toLocaleString() + ".xls";
                link.download = fileName;
                const blob = new Blob([byteArray], {type: 'application/vnd.ms-excel'});
                link.href = URL.createObjectURL(blob);
                link.click();
                URL.revokeObjectURL(link.href);
                setErrorMessage("Success! Your RFQ report has been generated and downloaded.")
            },
            error => {
                if (error.response && error.response.status === 400) {
                    console.log(error.response.data)
                    setErrorMessage(error.response.data);
                    setApiFinished(true);
                }
            }
        )
    };

    return (
        <div className="bg-image">
            <div className="container-fluid">
                <div style={{ marginTop: "20px", marginBottom: "15px" }}>
                    <p className="dashboard-text container-fluid">
                        Upload your lane data using our Excel template to generate custom RFQ reports. Download the template, fill it with your lane information, and upload to receive detailed analysis.
                    </p>
                </div>

                {/* Template Download Section */}
                <div className="card mb-4">
                    <div className="card-header" style={{ backgroundColor: '#f8f9fa', borderBottom: '1px solid #dee2e6' }}>
                        <h5 className="mb-0" style={{ color: '#495057' }}>
                            📥 Download Template
                        </h5>
                    </div>
                    <div className="card-body">
                        <div className="row align-items-center">
                            <div className="col-md-8">
                                <p className="mb-2">
                                    <strong>Download our official Excel template</strong> to ensure your data is formatted correctly for processing.
                                </p>
                                <div className="template-info">
                                    <div className="row">
                                        <div className="col-md-6">
                                            <div className="plan-info">
                                                <span className="plan-badge gold">🥇 Gold Plan</span>
                                                <span className="plan-detail">Up to 50 lanes per RFQ</span>
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="plan-info">
                                                <span className="plan-badge platinum">💎 Platinum Plan</span>
                                                <span className="plan-detail">Up to 100 lanes per RFQ</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-4 text-center">
                                <a 
                                    href="template.xlsx" 
                                    download 
                                    className="btn btn-lg"
                                    style={{
                                        backgroundColor: '#ff8c00',
                                        color: 'black',
                                        fontWeight: '700',
                                        borderRadius: '8px',
                                        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15)',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px',
                                        border: 'none',
                                        padding: '12px 30px',
                                        fontSize: '16px',
                                        textDecoration: 'none',
                                        display: 'inline-block'
                                    }}
                                >
                                    Download Template
                                </a>
                            </div>
                        </div>
                    </div>
                </div>

                {/* File Upload Section */}
                <div className="card mb-4">
                    <div className="card-header" style={{ backgroundColor: '#f8f9fa', borderBottom: '1px solid #dee2e6' }}>
                        <h5 className="mb-0" style={{ color: '#495057' }}>
                            📤 Upload Your Data
                        </h5>
                    </div>
                    <div className="card-body">
                        {!apiFinished && (
                            <div className="text-center p-4">
                                <PageLoader />
                                <p className="mt-3 text-muted">Processing your file...</p>
                            </div>
                        )}
                        
                        {apiFinished && (
                            <div className="upload-area">
                                <FileUploader 
                                    handleChange={handleChange} 
                                    name="file" 
                                    types={fileTypes}
                                />
                                <div className="upload-instructions mt-3">
                                    <p className="text-muted mb-2">
                                        <strong>Supported formats:</strong> XLS, XLSX
                                    </p>
                                    <p className="text-muted mb-0">
                                        <strong>Instructions:</strong> Drag and drop your Excel file here, or click to browse
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Status Messages */}
                {errorMessage.length > 0 && (
                    <div className="card mb-4">
                        <div className="card-body">
                            <div className={errorMessage.includes("downloaded") ? "alert alert-success" : "alert alert-danger"}>
                                <div className="d-flex align-items-center">
                                    {errorMessage.includes("downloaded") ? (
                                        <span className="me-2">✅</span>
                                    ) : (
                                        <span className="me-2">⚠️</span>
                                    )}
                                    <span>{errorMessage}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Requirements Section */}
                <div className="card">
                    <div className="card-header" style={{ backgroundColor: '#f8f9fa', borderBottom: '1px solid #dee2e6' }}>
                        <h5 className="mb-0" style={{ color: '#495057' }}>
                            📋 Requirements & Guidelines
                        </h5>
                    </div>
                    <div className="card-body">
                        <div className="requirements-list">
                            <div className="requirement-item">
                                <div className="requirement-icon">📋</div>
                                <div className="requirement-content">
                                    <strong>Use Official Template:</strong> Only use the official Farelanes template for guaranteed processing
                                </div>
                            </div>
                            <div className="requirement-item">
                                <div className="requirement-icon">🔒</div>
                                <div className="requirement-content">
                                    <strong>Data Validation:</strong> Ensure all required fields are filled correctly
                                </div>
                            </div>
                            <div className="requirement-item">
                                <div className="requirement-icon">📊</div>
                                <div className="requirement-content">
                                    <strong>Format Compliance:</strong> Follow the exact column structure and data types specified
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default DragDrop;